import {
  apiGetBlob,
  apiPostFormWithUploadProgress,
  apiPostJsonWithUploadProgress,
  type UploadProgressHandlers,
} from "./http";

export type MemberPointsImportCategoryColumn = {
  categoryKey: string;
  name: string;
  header: string;
};

export type MemberPointsImportCategoryCount = {
  categoryKey: string;
  categoryName: string;
  count: number;
};

export type MemberPointsImportAnalyzeRow = {
  row: number;
  idNumber: string;
  points: number;
  memo: string;
  activityLegacyId: string;
  memberLegacyId: string | null;
  matchedMemberNo: string | null;
  matchedMemberName: string | null;
  currentPoints: number | null;
  activityTitle: string | null;
  categoryParticipations: MemberPointsImportCategoryCount[];
  remark: string;
  defaultSelected: boolean;
};

export type MemberPointsImportAnalyzeResult = {
  rows: MemberPointsImportAnalyzeRow[];
  recommendedHeaders: string[];
  categoryColumns: MemberPointsImportCategoryColumn[];
  unrecognizedHeaders: string[];
};

export type MemberPointsImportCommitRowPayload = {
  row: number;
  memberLegacyId: string;
  points: number;
  memo: string;
  activityLegacyId?: string;
  categoryParticipations?: MemberPointsImportCategoryCount[];
};

export type MemberPointsImportDetailRow = {
  row: number;
  memberLegacyId: string;
  memberNo: string;
  memberName: string;
  points: number;
  balanceBefore: number;
  balanceAfter: number;
  memo: string;
  relatedActivityLegacyId: string | null;
  categoryParticipations?: Array<MemberPointsImportCategoryCount & { added?: number; total?: number }>;
};

export type MemberPointsImportCommitResult =
  | {
      ok: true;
      succeeded: number;
      totalPoints: number;
      errors: [];
      details: MemberPointsImportDetailRow[];
    }
  | {
      ok: false;
      succeeded: number;
      totalPoints: number;
      errors: { row: number; message: string }[];
      details: MemberPointsImportDetailRow[];
    };

export async function downloadMemberPointsImportTemplate(tenantLegacyId: string): Promise<Blob> {
  return apiGetBlob(
    `/api/tenants/${encodeURIComponent(tenantLegacyId)}/members/points-import/template`,
  );
}

export async function analyzeMemberPointsImport(
  tenantLegacyId: string,
  file: File,
  handlers?: UploadProgressHandlers,
): Promise<MemberPointsImportAnalyzeResult> {
  const fd = new FormData();
  fd.append("file", file);
  return apiPostFormWithUploadProgress<MemberPointsImportAnalyzeResult>(
    `/api/tenants/${encodeURIComponent(tenantLegacyId)}/members/points-import/analyze`,
    fd,
    handlers,
  );
}

export async function commitMemberPointsImport(
  tenantLegacyId: string,
  rows: MemberPointsImportCommitRowPayload[],
  handlers?: UploadProgressHandlers,
): Promise<MemberPointsImportCommitResult> {
  return apiPostJsonWithUploadProgress<MemberPointsImportCommitResult>(
    `/api/tenants/${encodeURIComponent(tenantLegacyId)}/members/points-import/commit`,
    { rows },
    handlers,
  );
}
